import React, { useState, useEffect } from 'react';
import db from '../db';

/**
 * CachedImage Component
 * Displays images with offline caching support
 * - When online: loads from server and caches the image
 * - When offline: loads from IndexedDB cache
 */
const CachedImage = ({ src, alt, className, style, onLoad, onError, ...props }) => {
  const [imageSrc, setImageSrc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let isMounted = true;
    let objectUrl = null;

    const setImageFromBlob = (blob) => {
      if (!isMounted || !blob) return;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
      objectUrl = URL.createObjectURL(blob);
      setImageSrc(objectUrl);
      setLoading(false);
      onLoad && onLoad();
    };

    const loadImage = async () => {
      if (!src) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(false);

        // Always try cache first to avoid unnecessary fetches
        const cachedBlob = await db.getCachedImage(src);
        if (cachedBlob) {
          setImageFromBlob(cachedBlob);
          // If the image is cached, skip re-downloading to prevent refresh flicker
          return;
        }

        const isOnline = navigator.onLine;

        if (!isOnline) {
          console.warn(`No cached image available and offline: ${src}`);
          setError(true);
          setLoading(false);
          onError && onError({ target: { style: {}, parentElement: { style: {} } } });
          return;
        }

        try {
          const response = await fetch(src);
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const blob = await response.blob();
          await db.cacheImage(src, blob);
          setImageFromBlob(blob);
        } catch (fetchError) {
          console.warn(`Failed to fetch image from server: ${src}`, fetchError);
          setError(true);
          setLoading(false);
          onError && onError({ target: { style: {}, parentElement: { style: {} } } });
        }
      } catch (err) {
        console.error('Error loading image:', err);
        if (isMounted) {
          setError(true);
          setLoading(false);
          onError && onError({ target: { style: {}, parentElement: { style: {} } } });
        }
      }
    };

    loadImage();

    // Cleanup: revoke object URL when component unmounts
    return () => {
      isMounted = false;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [src, onLoad, onError]);

  // Show placeholder while loading
  if (loading) {
    return (
      <div
        className={className}
        style={{
          ...style,
          backgroundColor: '#f0f0f0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100px'
        }}
        {...props}
      >
        <span style={{ color: '#999', fontSize: '12px' }}>Loading...</span>
      </div>
    );
  }

  // Show error placeholder if image failed to load
  if (error || !imageSrc) {
    return (
      <div
        className={className}
        style={{
          ...style,
          backgroundColor: '#f0f0f0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100px'
        }}
        {...props}
      >
        <span style={{ color: '#999', fontSize: '12px' }}>📷</span>
      </div>
    );
  }

  // Render the image
  return (
    <img
      src={imageSrc}
      alt={alt}
      className={className}
      style={style}
      {...props}
    />
  );
};

export default CachedImage;
